import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "./Projects";
import { CreditCard, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoiceDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { data: inv } = useSWR(`/invoices/${id}`, fetcher);
  const [polling, setPolling] = useState(false);

  const checkout = async () => {
    try {
      const r = await api.post("/payments/checkout", { invoice_id: id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch { toast.error("Stripe checkout failed"); }
  };

  // Poll on return from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Payment received!");
          setPolling(false);
          params.delete("session_id"); setParams(params, { replace: true });
          mutate(`/invoices/${id}`);
          return;
        }
        if (r.data.status === "expired" || attempts >= 8) {
          setPolling(false);
          return;
        }
        setTimeout(tick, 2000);
      } catch { setPolling(false); }
    };
    tick();
    // eslint-disable-next-line
  }, []);

  if (!inv) return <div className="p-10 label-eyebrow text-zinc-500">Loading…</div>;

  return (
    <div data-testid="invoice-detail-page">
      <PageHeader
        eyebrow={`// INVOICE ${inv.invoice_number}`}
        title={inv.customer_name}
        sub={inv.address}
        action={
          <div className="flex items-center gap-3">
            <Badge status={inv.status} />
            {inv.status !== "paid" && (
              <Button data-testid="pay-stripe-button" onClick={checkout} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
                <CreditCard size={14} className="mr-2" /> Pay with Stripe
              </Button>
            )}
            <Button data-testid="print-invoice" variant="outline" onClick={() => window.print()} className="rounded-none border-zinc-300 hover:bg-zinc-900 hover:text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
              <Printer size={14} className="mr-2" /> Print / PDF
            </Button>
          </div>
        }
      />
      <PageBody>
        {polling && (
          <div className="mb-6 border-l-4 border-orange-600 bg-orange-50 px-5 py-3 mono text-xs text-orange-900">
            CHECKING PAYMENT STATUS… (Stripe webhook + polling)
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-zinc-200">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
              <div className="col-span-6">Item</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Unit $</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
            {inv.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 px-5 py-3 border-b border-zinc-100 items-center text-sm">
                <div className="col-span-6 font-medium">{l.label}<span className="text-zinc-500 ml-2 mono text-xs">{l.unit}</span></div>
                <div className="col-span-2 text-right mono">{l.qty}</div>
                <div className="col-span-2 text-right mono">{fmt$(l.unit_price)}</div>
                <div className="col-span-2 text-right mono font-semibold">{fmt$(l.total)}</div>
              </div>
            ))}
          </div>

          <aside className="border border-zinc-200 h-fit">
            <div className="border-b border-zinc-200 px-5 py-3"><p className="label-eyebrow text-zinc-500">Summary</p></div>
            <div className="p-5 space-y-3">
              <SumRow k="ISSUE" v={inv.issue_date} />
              <SumRow k="DUE" v={inv.due_date} />
              <SumRow k="SUBTOTAL" v={fmt$(inv.subtotal)} />
              <SumRow k={`TAX (${inv.tax_pct}%)`} v={fmt$(inv.total - inv.subtotal)} />
              <div className="border-t border-zinc-300 pt-3">
                <p className="label-eyebrow text-zinc-500">AMOUNT DUE</p>
                <p data-testid="invoice-total" className="font-display font-black text-3xl mt-2 text-orange-600 tracking-tighter">{fmt$(inv.total)}</p>
              </div>
              {inv.paid_at && <p className="mono text-xs text-green-700">PAID · {inv.paid_at?.slice(0,10)}</p>}
            </div>
          </aside>
        </div>
      </PageBody>
    </div>
  );
}

function SumRow({ k, v }) {
  return (
    <div className="flex justify-between text-sm border-b border-zinc-100 pb-2 last:border-b-0">
      <span className="label-eyebrow text-zinc-500">{k}</span>
      <span className="mono text-zinc-900">{v}</span>
    </div>
  );
}
